import React, { useState } from 'react';
import api from '../../services/api';

export default function LogsTable({ 
  logs, 
  loading, 
  pagination, 
  onLoadMore, 
  onRefresh 
}) {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getActionBadgeClass = (action) => {
    const badgeClasses = {
      // Success - Verde
      'login_success': 'bg-success',
      'create_adolescente': 'bg-success',
      'create_movimentacao': 'bg-success',
      'create_atendimento': 'bg-success',
      'create_user': 'bg-success',
      
      // Info - Azul
      'view_adolescente': 'bg-info',
      'view_movimentacao': 'bg-info',
      'view_dashboard': 'bg-info',
      'search_adolescentes': 'bg-info',
      'search_system_logs': 'bg-info',
      'export_report': 'bg-info',
      
      // Warning - Amarelo
      'update_adolescente': 'bg-warning',
      'update_movimentacao': 'bg-warning',
      'update_atendimento': 'bg-warning',
      'update_user': 'bg-warning',
      'change_permissions': 'bg-warning',
      'password_reset': 'bg-warning',
      
      // Danger - Vermelho
      'login_failed': 'bg-danger',
      'delete_adolescente': 'bg-danger',
      'deactivate_user': 'bg-danger',
      'system_error': 'bg-danger',
      'validation_error': 'bg-danger',
      'permission_denied': 'bg-danger',
      
      // Secondary - Cinza
      'logout': 'bg-secondary'
    };

    return badgeClasses[action] || 'bg-secondary';
  };

  const showLogDetails = async (log) => {
    setSelectedLog(log);
    setDetailsError('');
    setLoadingDetails(true);
    setShowDetails(true);

    try {
      const response = await api.get(`/admin/logs/${log.id}`);
      const detailedLog = response?.data?.data?.log;
      if (detailedLog) {
        setSelectedLog(detailedLog);
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes do log:', error);
      setDetailsError('Não foi possível carregar os detalhes completos deste log.');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setSelectedLog(null);
    setShowDetails(false);
    setLoadingDetails(false);
    setDetailsError('');
  };

  const renderChangesSummary = (log) => {
    const changes = log?.changes;

    if (!changes || typeof changes !== 'object') {
      return log?.change_description || '-';
    }
    
    if (changes.changed_fields && Array.isArray(changes.changed_fields)) {
      return changes.changed_fields.join(', ');
    }
    
    if (changes.input && Object.keys(changes.input).length > 0) {
      return `${Object.keys(changes.input).length} campos alterados`;
    }
    
    return log?.change_description || 'Dados modificados';
  };

  const getEntityData = (log) => {
    const entityType = log?.entity_type || log?.entity?.type || null;
    const entityId = log?.entity_id ?? log?.entity?.id ?? null;

    return { entityType, entityId };
  };

  const hasMoreData = pagination.current_page < pagination.last_page;

  return (
    <>
      <div className="card logs-table-card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="bi bi-table me-2"></i>
              Logs do Sistema
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <small className="text-muted">
                {pagination.total > 0 
                  ? `${pagination.total.toLocaleString()} registros total`
                  : 'Nenhum registro encontrado'
                }
              </small>
              <button
                className="btn btn-sm btn-outline-success"
                onClick={onRefresh}
                disabled={loading}
              >
                <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
              </button>
            </div>
          </div>
        </div>
        
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-dark mb-0 logs-table">
              <thead className="table-header">
                <tr>
                  <th style={{ width: '130px' }}>Data/Hora</th>
                  <th style={{ width: '110px' }}>Usuário</th>
                  <th style={{ width: '150px' }}>Ação</th>
                  <th style={{ width: '110px' }}>Entidade</th>
                  <th>Alterações</th>
                  <th style={{ width: '100px' }}>IP Address</th>
                  <th style={{ width: '70px' }}>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {loading && logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="d-flex justify-content-center align-items-center">
                        <div className="spinner-border spinner-border-sm text-success me-2"></div>
                        Carregando logs...
                      </div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      <i className="bi bi-inbox display-4 d-block mb-2"></i>
                      Nenhum log encontrado com os filtros aplicados
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const { entityType, entityId } = getEntityData(log);
                    const userName = log.user?.name || log.user_name || 'Sistema';

                    return (
                      <tr key={log.id} className="log-row">
                        <td>
                          <div className="small">
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" title={userName}>
                            {userName || (
                              <span className="text-muted">Sistema</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getActionBadgeClass(log.action)} action-badge`}>
                            {log.action_name || log.action}
                          </span>
                          {log.is_critical && (
                            <i className="bi bi-shield-exclamation text-warning ms-1" title="Crítico"></i>
                          )}
                          {log.is_error && (
                            <i className="bi bi-exclamation-triangle text-danger ms-1" title="Erro"></i>
                          )}
                        </td>
                        <td>
                          <div className="small text-truncate" title={entityType || '-'}>
                            {entityType ? entityType.split('\\').pop() : '-'}
                            {entityId && (
                              <div className="text-muted tiny">ID: {entityId}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate small" title={renderChangesSummary(log)}>
                            {renderChangesSummary(log)}
                          </div>
                        </td>
                        <td>
                          <div className="small font-monospace">
                            {log.ip_address || '-'}
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => showLogDetails(log)}
                            title="Ver detalhes"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Load More Button */}
          {hasMoreData && (
            <div className="text-center p-3 border-top">
              <button
                className="btn btn-outline-success"
                onClick={onLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2"></div>
                    Carregando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-down me-2"></i>
                    Carregar mais logs
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {showDetails && selectedLog && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content bg-dark">
              <div className="modal-header border-bottom border-secondary">
                <h5 className="modal-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Detalhes do Log #{selectedLog.id}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeDetails}
                ></button>
              </div>
              <div className="modal-body">
                {loadingDetails ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-success me-2"></div>
                    Carregando detalhes do log...
                  </div>
                ) : (
                  <div className="row">
                    {detailsError && (
                      <div className="col-12">
                        <div className="alert alert-warning py-2" role="alert">
                          {detailsError}
                        </div>
                      </div>
                    )}
                    <div className="col-md-6">
                      <strong>Data/Hora:</strong>
                      <p>{formatDate(selectedLog.created_at)}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Usuário:</strong>
                      <p>{selectedLog.user?.name || selectedLog.user_name || 'Sistema'} {selectedLog.user_id && `(ID: ${selectedLog.user_id})`}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Ação:</strong>
                      <p>
                        <span className={`badge ${getActionBadgeClass(selectedLog.action)}`}>
                          {selectedLog.action_name || selectedLog.action}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <strong>Endereço IP:</strong>
                      <p className="font-monospace">{selectedLog.ip_address || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Sessão:</strong>
                      <p className="font-monospace small">{selectedLog.session_id || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <strong>Entidade:</strong>
                      <p>
                        {selectedLog.entity_type || selectedLog.entity?.type || '-'}
                        {(selectedLog.entity_id ?? selectedLog.entity?.id) && ` (ID: ${selectedLog.entity_id ?? selectedLog.entity?.id})`}
                      </p>
                    </div>
                    <div className="col-12">
                      <strong>User Agent:</strong>
                      <p className="small font-monospace">{selectedLog.user_agent || '-'}</p>
                    </div>
                    {selectedLog.changes && (
                      <div className="col-12">
                        <strong>Dados das Alterações:</strong>
                        <pre className="bg-secondary bg-opacity-25 p-3 rounded mt-2 small">
                          {JSON.stringify(selectedLog.changes, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.metadata && (
                      <div className="col-12">
                        <strong>Metadados:</strong>
                        <pre className="bg-secondary bg-opacity-25 p-3 rounded mt-2 small">
                          {JSON.stringify(selectedLog.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer border-top border-secondary">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDetails}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}

      <style>{`
        .logs-table-card {
          background: rgba(18, 26, 23, 0.8);
          border: 1px solid rgba(132, 169, 140, 0.15);
          border-radius: 12px;
        }
        
        .logs-table-card .card-header {
          background: rgba(132, 169, 140, 0.1);
          border-bottom: 1px solid rgba(132, 169, 140, 0.15);
          padding: 1rem;
        }
        
        .logs-table-card .card-title {
          color: #84a98c;
          font-weight: 600;
        }
        
        .logs-table {
          --bs-table-bg: transparent;
          table-layout: fixed;
          width: 100%;
        }
        
        .logs-table .table-header th {
          background: rgba(132, 169, 140, 0.1);
          color: #84a98c;
          border-bottom: 2px solid rgba(132, 169, 140, 0.3);
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .logs-table tbody tr:hover {
          background: rgba(132, 169, 140, 0.05);
        }
        
        .logs-table td {
          vertical-align: middle;
          border-bottom: 1px solid rgba(132, 169, 140, 0.1);
          padding: 0.75rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .action-badge {
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .tiny {
          font-size: 0.7rem;
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .modal-content {
          background: #1a1a1a !important;
          border: 1px solid rgba(132, 169, 140, 0.3);
        }
        
        .modal-header {
          color: #84a98c;
        }
        
        .modal-body strong {
          color: rgba(255, 255, 255, 0.8);
        }
        
        .modal-body p {
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 0.5rem;
        }
        
        pre {
          font-size: 0.8rem;
          max-height: 200px;
          overflow-y: auto;
        }
        
        .border-top {
          border-color: rgba(132, 169, 140, 0.15) !important;
        }
        
        .border-bottom {
          border-color: rgba(132, 169, 140, 0.15) !important;
        }
      `}</style>
    </>
  );
}
